/**
 * Removes PayPal from Shopify's express checkout buttons while "Ocultar botón de PayPal" is on
 * (loaded by `snippets/lomar-ocultar-paypal.liquid`).
 *
 * Hiding the PayPal button with CSS leaves its slot empty, because Shopify lays the buttons out
 * from the element's `wallet-configs` list. So each express checkout element whose list includes
 * PayPal is swapped for a copy without it, and Shopify spreads the remaining buttons over the full
 * width. An element with PayPal as its only wallet is left to the CSS rule, which hides it.
 * Copies are made once: they no longer list PayPal. Cart re-renders that bring a new element are
 * picked up by the observer.
 */
(() => {
  const SELECTOR = 'shopify-accelerated-checkout-cart[wallet-configs], shopify-accelerated-checkout[wallet-configs]';

  /** @param {Element} element */
  const removePaypal = (element) => {
    let wallets;
    try {
      wallets = JSON.parse(element.getAttribute('wallet-configs') ?? '[]');
    } catch {
      return;
    }
    if (!Array.isArray(wallets)) return;

    const remaining = wallets.filter((wallet) => wallet?.name !== 'paypal');
    if (remaining.length === wallets.length || remaining.length === 0) return;

    const copy = element.cloneNode(false);
    if (!(copy instanceof Element)) return;
    copy.setAttribute('wallet-configs', JSON.stringify(remaining));
    element.replaceWith(copy);
  };

  /** @param {ParentNode} root */
  const scan = (root) => {
    for (const element of root.querySelectorAll(SELECTOR)) removePaypal(element);
  };

  const start = () => {
    scan(document);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(SELECTOR)) removePaypal(node);
          else if (node.firstElementChild) scan(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
